/**
 * useSourceScheduleImpact.ts — FEAT-2026-0012
 *
 * Shared hook powering the quote/work-order ↔ scenario-planning sync UX.
 * Fetches the schedule-impact summary for a source (Quote or WorkOrder),
 * exposes a sync action, and builds the warn-before-delete ConfirmDialog state
 * (plain cascade vs. strong finalized-release).  Used by both QuoteEditorPage
 * and WorkOrderDetail so the warn-then-cascade logic lives in one place.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ConfirmDialogState } from '../components/shared/ConfirmDialog';
import {
  getSourceScheduleImpact,
  syncSourceSchedule,
  type ScenarioSourceType,
  type SourceScheduleImpact,
  type SourceSyncResult,
} from '../services/scheduleScenario';

/**
 * W2-4 (P-20b): discriminated "sync refused" result — the backend answered 409
 * SYNC_RELEASES_COMMITTED because the prune would release committed (live)
 * bookings. Callers confirm via ConfirmDialog and retry with syncConfirmed().
 */
export interface SyncBlockedResult {
  blocked: true;
  committedBookings: number;
  taskNames: string[];
}

/** Type guard: did sync() come back blocked (needs the Release & sync confirm)? */
export function isSyncBlocked(
  result: SourceSyncResult | SyncBlockedResult,
): result is SyncBlockedResult {
  return (result as SyncBlockedResult).blocked === true;
}

/** Parse an axios error into a SyncBlockedResult, or null when it is something else. */
function parseSyncBlocked(err: unknown): SyncBlockedResult | null {
  const axiosErr = err as {
    response?: {
      status?: number;
      data?: { code?: string; committedBookings?: number; taskNames?: string[] };
    };
  };
  if (
    axiosErr?.response?.status === 409 &&
    axiosErr.response.data?.code === 'SYNC_RELEASES_COMMITTED'
  ) {
    return {
      blocked: true,
      committedBookings: axiosErr.response.data.committedBookings ?? 0,
      taskNames: axiosErr.response.data.taskNames ?? [],
    };
  }
  return null;
}

/**
 * P2-11: shared copy for a FAILED sync. The pipeline is transactional per
 * step (generation → prune → respan), each step idempotent — so after a
 * mid-pipeline failure re-running Sync converges the rest. Say so instead of
 * a dead-end "failed".
 */
export const SYNC_FAILED_MESSAGE =
  'Sync failed partway — nothing is half-written, but later steps may not have run. ' +
  'Run "Sync now" again to converge the schedule.';

/**
 * Shared snackbar copy for a completed sync (both QuoteEditorPage and
 * WorkOrderDetail render it — one builder so the wording can't drift).
 */
export function buildSyncSnackbar(
  result: SourceSyncResult,
): { severity: 'success' | 'warning'; message: string } {
  const added = result.tasksAdded ?? 0;
  const updated = result.tasksUpdated ?? 0;
  const removed = result.tasksRemoved ?? 0;
  // P-20a: surface a committed-work release truthfully (informational only).
  const committed = result.committedBookingsRemoved ?? 0;
  const releasedNote = committed > 0 ? ` Released ${committed} committed booking(s).` : '';
  // W2-3: sync now converges tentative bookings to the current qty/duration.
  const respanned = result.bookingsRespanned ?? 0;
  const healed = result.staffingHealed ?? 0;
  const respanNote =
    (respanned > 0 ? ` ${respanned} booking(s) re-spanned.` : '') +
    (healed > 0 ? ` ${healed} booking(s) had stale staffing repaired.` : '');
  // W2-3: committed bookings are never silently resized — flag for follow-up.
  const driftTasks = result.committedDriftTasks ?? [];
  const driftNote = driftTasks.length > 0
    ? ` ${driftTasks.length} committed booking(s) still reflect old values — manual follow-up: ${driftTasks.join(', ')}.`
    : '';
  return {
    severity: driftTasks.length > 0 ? 'warning' : 'success',
    message: `Schedule synced: +${added} / ~${updated} / −${removed}.${releasedNote}${respanNote}${driftNote}`,
  };
}

/**
 * W2-4 (P-20b): the confirm ceremony shown when sync() came back blocked.
 * Cancel (the default button) is a no-op; the destructive confirm re-runs the
 * sync with confirmRelease=true via the caller-supplied onConfirm.
 */
export function buildSyncReleaseDialog(
  blocked: SyncBlockedResult,
  onConfirm: () => void | Promise<void>,
): ConfirmDialogState {
  const taskList = blocked.taskNames.map((n) => `• ${n}`).join('\n');
  return {
    open: true,
    title: 'Sync now — committed work affected',
    message:
      `Syncing will remove task(s) whose source lines were deleted or changed — releasing ` +
      `${blocked.committedBookings} committed booking(s) from the live schedule:\n\n` +
      `${taskList}\n\n` +
      'This cannot be undone. Choose "Release & sync" to continue.',
    confirmLabel: 'Release & sync',
    confirmColor: 'error',
    onConfirm: () => { void onConfirm(); },
  };
}

interface UseSourceScheduleImpactResult {
  impact: SourceScheduleImpact | null;
  loading: boolean;
  syncing: boolean;
  /** Re-fetch the impact (e.g. after a sync or a line-item change). */
  refetch: () => Promise<SourceScheduleImpact | null>;
  /**
   * Run a source-sync, then refetch. Returns the change counts (or null on
   * failure). W2-4: when the sync would release committed bookings, resolves to
   * a SyncBlockedResult instead of throwing — raise buildSyncReleaseDialog and
   * retry via syncConfirmed().
   */
  sync: () => Promise<SourceSyncResult | SyncBlockedResult | null>;
  /** W2-4: the confirmed retry — syncs with confirmRelease=true, then refetches. */
  syncConfirmed: () => Promise<SourceSyncResult | null>;
  /**
   * Build the delete-confirm dialog state for this source.
   * - No scenario → returns null (caller keeps its existing plain confirm).
   * - Scenario, not finalized → cascade warning, single confirm → onDelete(false).
   * - Finalized → strong warning; the destructive release is the *secondary*
   *   action (requires an explicit second click) → onDelete(true).
   */
  buildDeleteDialog: (args: {
    sourceLabel: string;
    onDelete: (releaseSchedule: boolean) => void | Promise<void>;
  }) => ConfirmDialogState | null;
}

export function useSourceScheduleImpact(
  source: ScenarioSourceType,
  sourceKey: string | undefined,
): UseSourceScheduleImpactResult {
  const [impact, setImpact] = useState<SourceScheduleImpact | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refetch = useCallback(async () => {
    if (!sourceKey) {
      setImpact(null);
      return null;
    }
    setLoading(true);
    try {
      const data = await getSourceScheduleImpact(source, sourceKey);
      setImpact(data);
      return data;
    } catch {
      // Non-fatal: leave the banner hidden and let delete fall back to plain
      // confirm. Never block the page on a failed impact fetch.
      setImpact(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [source, sourceKey]);

  useEffect(() => { refetch(); }, [refetch]);

  const runSync = useCallback(async (confirmRelease: boolean) => {
    if (!sourceKey) return null;
    setSyncing(true);
    try {
      const result = await syncSourceSchedule(
        source,
        sourceKey,
        undefined,
        confirmRelease ? { confirmRelease: true } : undefined,
      );
      await refetch();
      return result;
    } finally {
      setSyncing(false);
    }
  }, [source, sourceKey, refetch]);

  const sync = useCallback(async (): Promise<SourceSyncResult | SyncBlockedResult | null> => {
    try {
      return await runSync(false);
    } catch (err) {
      // W2-4: the committed-release 409 is an expected flow, not a failure —
      // must NOT fall into the callers' generic catch (it would read as a
      // silent/failed sync). Everything else still throws.
      const blocked = parseSyncBlocked(err);
      if (blocked) return blocked;
      throw err;
    }
  }, [runSync]);

  const syncConfirmed = useCallback(() => runSync(true), [runSync]);

  const buildDeleteDialog = useCallback<UseSourceScheduleImpactResult['buildDeleteDialog']>(
    ({ sourceLabel, onDelete }) => {
      const noun = source === 'Quote' ? 'quote' : 'work order';

      // P2-9: the guard protects COMMITTED BOOKINGS, not Scenario rows. A source
      // scheduled straight onto BASELINE (chat/API generation, legacy) has no
      // Scenario row but its committed live work still needs the strong
      // release confirm — the backend now 409s (SOURCE_RELEASES_COMMITTED)
      // without the releaseSchedule opt-in, so a plain confirm would dead-end.
      if (!impact?.hasScenario) {
        const committed = impact?.sourceCommittedBookings ?? 0;
        if (committed === 0) return null; // caller keeps its existing simple confirm
        return {
          open: true,
          title: `Delete ${sourceLabel} — live schedule attached`,
          message:
            `${sourceLabel} has ${committed} committed booking(s) on the live master schedule.\n\n` +
            'Deleting this ' + noun + ' will RELEASE those committed bookings from the live schedule. ' +
            'This cannot be undone. Choose "Release & delete" to confirm the live release.',
          confirmLabel: 'Cancel delete',
          confirmColor: 'primary',
          // Confirm button is the SAFE path (cancel); the destructive release
          // lives on the explicit secondary button — same ceremony as below.
          onConfirm: () => { /* safe no-op: closes the dialog */ },
          secondaryLabel: `Release & delete ${noun}`,
          secondaryColor: 'error',
          onSecondary: () => { void onDelete(true); },
        };
      }
      const scenarioList = impact.scenarios
        .map((s) => `• ${s.code || s.name} — ${s.plannedTaskCount}/${s.taskCount} task(s) planned`)
        .join('\n');

      if (impact.hasFinalized) {
        // STRONG warning: a finalized scenario has live committed bookings on the
        // master schedule. The destructive release is the SECONDARY action so it
        // takes an explicit, deliberate second click.
        const committed = impact.totals.committedBookings;
        return {
          open: true,
          title: `Delete ${sourceLabel} — live schedule attached`,
          message:
            `${sourceLabel} has a FINALIZED (live) schedule with ${committed} committed booking(s) on the master schedule:\n\n` +
            `${scenarioList}\n\n` +
            'Deleting this ' + noun + ' will RELEASE those committed bookings from the live schedule. ' +
            'This cannot be undone. Choose "Release & delete" to confirm the live release.',
          confirmLabel: 'Cancel delete',
          confirmColor: 'primary',
          // Confirm button is the SAFE path here (cancel). The destructive
          // release lives on the explicit secondary button.
          onConfirm: () => { /* safe no-op: closes the dialog */ },
          secondaryLabel: `Release & delete ${noun}`,
          secondaryColor: 'error',
          onSecondary: () => { void onDelete(true); },
        };
      }

      // Normal cascade warning: attached scenario(s) will be removed too.
      return {
        open: true,
        title: `Delete ${sourceLabel}`,
        message:
          `${sourceLabel} has an attached schedule that will be removed too:\n\n` +
          `${scenarioList}\n\n` +
          'These tentative bookings and assignments will be released. This cannot be undone.',
        confirmLabel: `Delete ${noun} + schedule`,
        confirmColor: 'error',
        onConfirm: () => { void onDelete(false); },
      };
    },
    [impact, source],
  );

  return { impact, loading, syncing, refetch, sync, syncConfirmed, buildDeleteDialog };
}
